"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ShieldingYourSuccess() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [particles, setParticles] = useState<Array<{
    id: number;
    left: number;
    delay: number;
    duration: number;
    size: number;
  }>>([]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const generated = Array.from({
      length: 24
    }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 12 + Math.random() * 10,
      size: 1 + Math.random() * 3
    }));
    setParticles(generated);
  }, []);
  const stats = [{
    label: "FOCUS",
    value: "ESTATE LAW"
  }, {
    label: "LOCATION",
    value: "FLORIDA"
  }, {
    label: "STRATEGY",
    value: "PRESERVATION"
  }, {
    label: "CONSULT",
    value: "COMPLIMENTARY"
  }];
  return <section data-section-id="228" id="shielding-your-success" ref={sectionRef} className="relative bg-background text-foreground overflow-hidden min-h-[90vh] flex items-end dark">
      {}
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/florida_coastal_estate_dusk.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/florida_coastal_estate_dusk.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/florida_coastal_estate_dusk.png-wvc-sizes" alt="High-end Florida estate architecture at dusk with warm interior lighting" loading="lazy" decoding="async" className="w-full h-full object-cover animate-[slowZoom_25s_ease-in-out_infinite_alternate] opacity-60" />
        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(90deg, hsl(215, 45%, 10%, 0.98) 0%, hsl(215, 45%, 10%, 0.9) 30%, hsl(215, 45%, 10%, 0.75) 60%, hsl(215, 45%, 10%, 0.5) 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, hsl(215, 45%, 10%, 0.6) 0%, transparent 30%, transparent 60%, hsl(215, 45%, 10%, 0.8) 100%)"
      }} />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => <span key={p.id} data-index={i} className="absolute rounded-md bg-primary/20" style={{
        left: `${p.left}%`,
        bottom: `-10px`,
        width: `${p.size}px`,
        height: `${p.size}px`,
        animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
        filter: "blur(1px)"
      }} />)}
      </div>

      {}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <p className="font-mono text-[10px] tracking-[0.3em] text-secondary uppercase whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          FLORIDA ESTATE PLANNING &middot; ASSET PROTECTION &middot; 27.6648° N
        </p>
      </div>

      {}
      <div ref={contentRef} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 xl:pl-[120px] xl:pr-20 pt-32 pb-16 md:pb-20 lg:pb-24 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        

        {}
        <h1 className="font-default font-semibold text-foreground leading-[0.85] tracking-tighter mb-10 md:mb-12" style={{
        fontSize: "clamp(60px, 14vw, 160px)"
      }}>
          <span className="block">SHIELD</span>
          <span className="block pl-[0.5em]">YOUR</span>
          <span className="block italic text-primary">SUCCESS.</span>
        </h1>

        {}
        <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-[560px] mb-10 md:mb-12">
          Sophisticated wealth preservation for Florida families. We specialize exclusively in 
          Estate Planning and Asset Protection, ensuring your legacy remains private, 
          protected from creditors, and entirely outside the reach of probate courts.
        </p>

        {}
        <div className="mb-10 md:mb-12 max-w-3xl">
          <div className="flex flex-wrap items-stretch gap-y-4 border-t border-b border-border py-5">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className={`flex-1 min-w-[140px] px-4 md:px-5 ${i !== 0 ? "md:border-l border-border" : ""}`}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-secondary uppercase mb-1.5">
                  {stat.label}
                </p>
                <p className="font-mono text-sm md:text-base font-medium text-foreground tracking-wider">
                  {stat.value}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="flex flex-wrap items-center gap-4">
          <Button asChild size="lg" className="h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.2em] uppercase rounded-md shadow-lg">
            <Link to="/ContactPage">
              Schedule Audit
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-14 px-8 bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs tracking-[0.2em] uppercase rounded-md">
            <Link to="/AssetProtectionPage">
              <Users className="mr-1 size-4" />
              Asset Protection
            </Link>
          </Button>
        </div>
      </div>

      {}
      <style>{`
        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        @keyframes floatUp {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          15% {
            opacity: 0.4;
          }
          85% {
            opacity: 0.2;
          }
          100% {
            transform: translateY(-105vh) translateX(20px);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}